import type { PaginationState, SortingState } from '@tanstack/react-table';
import QueryString from 'qs';
import type { InferType } from 'yup';
import { array, boolean, date, mixed, number, object, string } from 'yup';

// eslint-disable-next-line import/no-cycle
import authService from '@/services/auth/auth';

import { CitySchema, DjangoDataSchema } from './common';
import { apiRoutes } from './config';

enum JobMatchingCandidateLanguageValue {
  BASIC,
  INTERMEDIATE,
  ADVANCED,
  NEAR_NATIVE,
  NATIVE,
}

export enum SeniorityLevel {
  INTERN = 'INTERN',
  JUNIOR = 'JUNIOR',
  MID = 'MID',
  SENIOR = 'SENIOR',
  LEAD = 'LEAD',
  SPECIALIST = 'SPECIALIST',
}

export enum PaymentPeriod {
  HOUR = 'HOUR',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export enum Currency {
  PLN = 'PLN',
  EUR = 'EUR',
  USD = 'USD',
}

export enum WorkMode {
  ON_SITE = 'ON_SITE',
  HYBRID = 'HYBRID',
  REMOTE = 'REMOTE',
}

export const WorkModeLabels = {
  [WorkMode.HYBRID]: 'Hybrid',
  [WorkMode.ON_SITE]: 'Onsite',
  [WorkMode.REMOTE]: 'Remote',
};

export const requiredFieldsSettingsKeys = [
  'skills',
  'wantToUseSkills',
  'languages',
  'commercialExperience',
  'country',
  'city',
  'phone',
  'githubUrl',
  'linkedinUrl',
  'portfolioUrl',
  'videoUrl',
  'coverLetter',
  'jobStart',
  'availability',
  'seniorityLevel',
  'relocation',
  'currency',
  'financialExpectationsMin',
  'financialExpectationsMax',
  'paymentPeriod',
  'onsite',
  'additionalQuestions',
];

export const disqualificationSettingsKeys = ['onsite'];

const getObjectShapeFromArray = (arr: string[]) =>
  arr.reduce(
    (schema, key) => ({
      ...schema,
      [key]: boolean().required(),
    }),
    {}
  );

export const requiredFieldsSettingsSchema = object()
  .shape(getObjectShapeFromArray(requiredFieldsSettingsKeys))
  .required();

export const disqualificationSettingsSchema = object()
  .shape(getObjectShapeFromArray(disqualificationSettingsKeys))
  .defined();

const offerAdminSchema = object({
  id: number().defined(),
  visible: boolean().required(),
  archived: boolean().required(),
  recruiteeId: number().nullable().defined(),
  recruiteeJobId: number().nullable().defined(),
  title: string().defined(),
  description: string().defined(),
  internalComment: string().defined(),
  workMode: mixed<WorkMode | ''>()
    .oneOf([...Object.values(WorkMode), ''])
    .defined(),
  seniorityLevel: array()
    .of(string<SeniorityLevel>().oneOf(Object.values(SeniorityLevel)).defined())
    .required(),
  minSalary: number().nullable().defined(),
  maxSalary: number().nullable().defined(),
  currency: mixed<Currency | ''>().oneOf([...Object.values(Currency), '']),
  paymentPeriod: mixed<PaymentPeriod | ''>().oneOf([
    ...Object.values(PaymentPeriod),
    '',
  ]),
  location: CitySchema.nullable().defined(),
  minYearsOfExperience: number().nullable().defined(),
  additionalQuestions: array().of(string().defined()).required().max(20),
  skills: array()
    .of(
      object({
        skill: object({
          id: number().required(),
          name: string().defined(),
        }).required(),
        minYearsOfExperience: number().nullable().defined(),
        isNiceToHave: boolean().required(),
      }).required()
    )
    .required(),
  languages: array()
    .of(
      object({
        language: object({
          id: number().required(),
          name: string().defined(),
          code: string().defined(),
        }).required(),
        level: number<JobMatchingCandidateLanguageValue>().nullable().defined(),
        isNiceToHave: boolean().required(),
      }).required()
    )
    .required(),
  disqualificationSettings: disqualificationSettingsSchema.nullable(),
  createdAt: date().defined(),
  updatedAt: date().defined(),
});

const recruiteeJobSchema = object({
  guid: string().defined(),
  id: string().defined(),
  title: string().defined(),
});

export type OfferAdmin = InferType<typeof offerAdminSchema>;

export const getOfferList = async ({
  pageIndex,
  pageSize,
  archived,
  search,
  sorting,
}: {
  archived?: boolean;
  search?: string;
  sorting: SortingState;
} & PaginationState) => {
  const { data } = await authService.get(apiRoutes.offerAdmin._root, {
    params: {
      limit: pageSize,
      offset: pageSize * pageIndex,
      archived,
      search,
      order: sorting.map(({ desc, id }) => `${desc ? '-' : ''}${id}`),
    },
    paramsSerializer: (params) =>
      QueryString.stringify(params, { arrayFormat: 'repeat' }),
  });
  const validatedData = await DjangoDataSchema(offerAdminSchema).validate(data);
  return {
    ...validatedData,
    results: validatedData.results.map((val) => transformOfferData(val)),
  };
};

export const transformOfferData = (data: OfferAdmin) => {
  return {
    ...data,
    workMode: data.workMode
      ? { label: WorkModeLabels[data.workMode], value: data.workMode }
      : undefined,
    currency: data.currency
      ? { label: data.currency, value: data.currency }
      : null,
    paymentPeriod: data.paymentPeriod
      ? { label: data.paymentPeriod, value: data.paymentPeriod }
      : null,
    location: data.location
      ? { label: data.location.name, value: data.location }
      : null,
    seniorityLevel: data.seniorityLevel.map((seniority) => ({
      label: seniority,
      value: seniority,
    })),
    disqualificationSettings:
      data.disqualificationSettings ??
      Object.fromEntries(
        disqualificationSettingsKeys.map((key) => [key, false])
      ),
  };
};

export const getOffer = async (id: number) => {
  const { data } = await authService.get(apiRoutes.offerAdmin.detail(id));
  const validatedData = await offerAdminSchema.validate(data);
  return transformOfferData(validatedData);
};

export const deleteOffer = async (id: number) =>
  authService.delete(apiRoutes.offerAdmin.detail(id));

export const getRecruiteeJobs = async () => {
  const { data } = await authService.get(apiRoutes.offerAdmin.recruiteeJobs);
  return array().of(recruiteeJobSchema).defined().validate(data);
};
